import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiUpload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, ImagePlus } from "lucide-react";

const PLATFORMS = ["Twitch", "YouTube", "Kick", "TikTok"];

export default function StreamerSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    handle: "", platform: "Twitch", channel_url: "",
    country: "US", bio: "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const fileRef = useRef(null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [busy, setBusy] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file."); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Image is too large (max 3 MB)."); return; }
    setAvatarFile(f);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/streamers/signup", form);
      if (avatarFile) {
        const fd = new FormData();
        fd.append("file", avatarFile);
        try {
          await apiUpload(`/streamers/${data.id}/avatar`, fd);
        } catch (upErr) {
          toast.error("Dossier saved, but avatar upload failed — you can retry from your dossier.");
        }
      }
      toast.success("Dossier created. Welcome to the broadcast wing.");
      nav(`/streamers/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="streamer-signup-root">
      <aside className="lg:col-span-5 relative">
        <div className="sticky top-24 border border-[#232330] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1760800185531-2b1da99d7538?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxnYW1pbmclMjB0b3VybmFtZW50JTIwdHJvcGh5fGVufDB8fHx8MTc4NTc1NTYwMXww&ixlib=rb-4.1.0&q=85"
            alt="dossier"
            className="w-full h-[60vh] object-cover"
          />
          <div className="p-6 bg-black/60 backdrop-blur-sm absolute inset-x-0 bottom-0">
            <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ ENLIST</div>
            <div className="font-heading text-3xl font-black uppercase tracking-tighter">
              Become a<br />Legacy streamer
            </div>
            <p className="text-sm text-gray-300 mt-3">
              Verified badge · exposure on the ticker · schedule in every fan&apos;s feed.
            </p>
          </div>
        </div>
      </aside>

      <form onSubmit={submit} className="lg:col-span-7 space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-400">Handle</Label>
          <Input data-testid="signup-handle" required value={form.handle} onChange={upd("handle")}
            className="rounded-none bg-[#101015] border-[#232330] focus:border-cyan-400 mt-2" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-400">Platform</Label>
          <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
            <SelectTrigger data-testid="signup-platform" className="rounded-none bg-[#101015] border-[#232330] mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#101015] border-[#232330] text-white">
              {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-400">Channel URL</Label>
            <Input data-testid="signup-channel" required value={form.channel_url} onChange={upd("channel_url")}
              placeholder="https://twitch.tv/yourhandle"
              className="rounded-none bg-[#101015] border-[#232330] focus:border-cyan-400 mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-400">Country (ISO-2)</Label>
            <Input data-testid="signup-country" required value={form.country} maxLength={2} onChange={upd("country")}
              className="rounded-none bg-[#101015] border-[#232330] focus:border-cyan-400 uppercase mt-2" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-400">Profile picture (optional)</Label>
          <div className="mt-2 flex items-center gap-4">
            <div className="w-20 h-20 border border-[#232330] bg-[#05050A] overflow-hidden flex items-center justify-center">
              {avatarPreview ? (
                <img src={avatarPreview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <ImagePlus className="w-6 h-6 text-gray-600" />
              )}
            </div>
            <input
              ref={fileRef}
              data-testid="signup-avatar-file"
              type="file"
              accept="image/*"
              onChange={pickFile}
              className="hidden"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="signup-avatar-btn"
              variant="outline"
              className="rounded-none border-[#232330] hover:border-cyan-400 uppercase tracking-widest text-xs font-bold"
            >
              <Upload className="w-3.5 h-3.5 mr-2" />
              {avatarFile ? avatarFile.name : "Upload picture"}
            </Button>
            {avatarFile && (
              <button
                type="button"
                onClick={() => { setAvatarFile(null); setAvatarPreview(""); if (fileRef.current) fileRef.current.value = ""; }}
                className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-red-500"
              >
                remove
              </button>
            )}
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mt-2">Max 3 MB · PNG or JPG</p>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-400">Dossier bio</Label>
          <Textarea
            data-testid="signup-bio"
            value={form.bio} onChange={upd("bio")}
            rows={5}
            placeholder="Playstyle, achievements, community vibe..."
            className="rounded-none bg-[#101015] border-[#232330] focus:border-cyan-400 mt-2"
          />
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="signup-submit"
          className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest px-8"
        >
          {busy ? "Filing dossier…" : "Submit dossier"}
        </Button>
      </form>
    </div>
  );
}
